/**
 * Trace Policy (TV1-1696, #18; flattened per-execution list TV1-1971)
 *
 * Handles two execution-level side effects for the trace modal:
 *
 *   1. Trace load — `ui/openTrace` sets `loading.traceLoading = true` and clears
 *      `data.traceExecution` (see handler). This policy reacts to that
 *      `false → true` transition: it resolves the execution row (preferring the
 *      already-loaded flat-list row in `data.executions`, else
 *      `RuleExecutionsById`), dispatches `data/setTrace`, then clears
 *      `loading.traceLoading`.
 *
 *   2. Retry — the inline "Reintentar" control on a failed row (and the trace
 *      modal's Retry button) flips `loading.retrying[executionId]` to `true` (the
 *      submission trigger; memory `feedback_cdse_submission_trigger_canapply`).
 *      This policy reacts to that `false → true` transition: calls
 *      `backend.commands.RetryRuleExecution`, then clears the per-execution retry
 *      flag. The flat list self-refreshes via the `executionsSubscriptionPolicy`
 *      signal-mode subscription (the retry emits new execution events), so no
 *      explicit read-after-write refetch is needed here.
 *
 * Mechanism note: the CDSE policy engine reacts to STATE CHANGES, not dispatched
 * commands. Both triggers are loading-flag transitions, mirroring the form-submit
 * trigger pattern. Errors are re-thrown to the global handler — no manual
 * notifications. A `closed` guard prevents late async writes after unmount.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { RulesExecutionsReadmodelType } from '@bett3r-dev/teselly-domain';
import { ActivityState } from '../state';
import { ActivityCommand } from '../commands';

type Dispatch = (
  command: ActivityCommand,
  meta: { source: string }
) => void;

/** executionIds whose `retrying` flag flipped false → true in this transition. */
const newlyRetrying = (
  prev: ActivityState,
  next: ActivityState
): string[] => {
  const prevRetrying = prev.loading.retrying;
  const nextRetrying = next.loading.retrying;
  return Object.keys( nextRetrying ).filter(
    ( id ) => nextRetrying[id] === true && prevRetrying[id] !== true
  );
};

export const createTracePolicy = (
  backend: ClientLibraryType
): Policy<ActivityState, ActivityCommand> => {
  let closed = false;

  /** Resolve a full execution row for the trace modal. */
  const loadTrace = async (
    state: ActivityState,
    dispatch: Dispatch
  ) => {
    const executionId = state.ui.traceExecutionId;
    // `ui.traceExecutionId` is the pending target set by the openTrace handler.
    // (state.data.traceExecution holds the resolved row; null until loaded.)
    if ( !executionId ) {
      // No target — clear loading and bail.
      dispatch(
        {
          type: 'automation/activity/loading/setTraceLoading',
          payload: { isLoading: false }
        },
        { source: 'api' }
      );
      return;
    }

    try {
      // Prefer the already-loaded flat-list row to avoid a round-trip.
      const fromList = state.data.executions.find(
        ( execution ) => execution.id === executionId
      );

      const execution: RulesExecutionsReadmodelType =
        fromList ?? await backend.queries.RuleExecutionsById( executionId );

      if ( closed ) return;

      dispatch(
        {
          type: 'automation/activity/data/setTrace',
          payload: { execution: execution ?? null }
        },
        { source: 'api' }
      );

      // Fetch the rule definition DSL so the trace can be shown as the dry-run
      // node-graph (TV1-1900). A DSL failure must NEVER block the trace modal:
      // on any error (incl. a deleted/404 definition) we fall back to the flat
      // text trace by dispatching `ruleDefinition: null` and swallowing the error.
      await loadTraceRuleDefinition( execution, dispatch );

      dispatch(
        {
          type: 'automation/activity/loading/setTraceLoading',
          payload: { isLoading: false }
        },
        { source: 'api' }
      );
    } catch ( error ) {
      if ( !closed ) {
        dispatch(
          {
            type: 'automation/activity/loading/setTraceLoading',
            payload: { isLoading: false }
          },
          { source: 'api' }
        );
      }
      throw error;
    }
  };

  /**
   * Resolve the rule definition DSL for an execution and push it into state.
   * Used by the trace modal to render the dry-run node-graph (TV1-1900).
   * Best-effort: any failure (missing `ruleDefinitionId`, fetch error, deleted
   * definition) resolves to `ruleDefinition: null`, which makes the modal fall
   * back to the flat-text trace. Never throws.
   */
  const loadTraceRuleDefinition = async (
    execution: RulesExecutionsReadmodelType | undefined,
    dispatch: Dispatch
  ) => {
    let ruleDefinition: unknown | null = null;
    const ruleDefinitionId = execution?.ruleDefinitionId;

    if ( ruleDefinitionId ) {
      try {
        const definition = await backend.queries.RuleDefinitionsById(
          ruleDefinitionId
        );
        ruleDefinition = ( definition as { ruleDefinition?: unknown } | null )?.ruleDefinition ?? null;
      } catch {
        // Swallow — fall back to flat-text trace.
        ruleDefinition = null;
      }
    }

    if ( closed ) return;

    dispatch(
      {
        type: 'automation/activity/data/setTraceRuleDefinition',
        payload: { ruleDefinition }
      },
      { source: 'api' }
    );
  };

  /**
   * Retry a single failed execution, then clear its retry flag. The flat list
   * self-refreshes via the `executionsSubscriptionPolicy` signal-mode
   * subscription (the retry emits new execution events), so no explicit
   * read-after-write refetch is done here.
   */
  const retryExecution = async (
    executionId: string,
    dispatch: Dispatch
  ) => {
    try {
      await backend.commands.RetryRuleExecution({ id: executionId });
    } finally {
      // Always clear the per-execution retry flag, even on error.
      if ( !closed ) {
        dispatch(
          {
            type: 'automation/activity/loading/setRetrying',
            payload: { executionId, isRetrying: false }
          },
          { source: 'api' }
        );
      }
    }
  };

  return {
    name: 'automation-activity-trace',

    shouldReact: ( prev, next ) => {
      // Trace load trigger: traceLoading false → true.
      const traceOpened =
        !prev.loading.traceLoading && next.loading.traceLoading;

      // Retry trigger: any retrying[id] false → true.
      const retryStarted = newlyRetrying( prev, next ).length > 0;

      return traceOpened || retryStarted;
    },

    onStateChange: async ({ prev, next, ctx }) => {
      // Trace load.
      if ( !prev.loading.traceLoading && next.loading.traceLoading ) {
        await loadTrace( next, ctx.dispatch );
      }

      // Retry (may coincide with, but is independent of, a trace load).
      const toRetry = newlyRetrying( prev, next );
      for ( const executionId of toRetry ) {
        await retryExecution( executionId, ctx.dispatch );
      }
    },

    cleanup: () => {
      closed = true;
    }
  };
};
